import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  RefreshCw,
  CheckCircle2,
  Undo2,
  Wallet,
  Ban,
  Trash2,
  FileSpreadsheet,
  AlertTriangle,
  Search,
  Plus,
  X,
  Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  getNomina,
  recalcularNomina,
  actualizarLineaNomina,
  cambiarEstadoNomina,
  eliminarNomina,
} from './Services/nomina.services';
import { exportarNominaExcel } from './utils/nominaExcel';
import {
  ESTADOS,
  GRUPOS_HORAS,
  ETIQUETA_TIPO,
  TIPOS_NOMINA,
  sumaGrupo,
  diaSemana,
  fechaCorta,
  horas,
  pesos,
} from './utils/nomina';

const TIPO_DIA = {
  ordinario: null,
  dominical: { label: 'Dominical', className: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-200' },
  festivo: { label: 'Festivo', className: 'bg-violet-100 text-violet-800 dark:bg-violet-500/20 dark:text-violet-200' },
};

// El personal temporal se liquida y se revisa aparte, así que la fila va resaltada.
// Las nóminas calculadas antes de que existiera el campo no lo traen: ahí no se afirma
// nada y se invita a recalcular.
const esTemporal = (linea) => String(linea?.empleado?.tipoNomina || '').toLowerCase() === 'temporal';

const TipoNomina = ({ tipo }) => {
  if (!tipo) {
    return (
      <span className="text-xs text-muted-foreground" title="La nómina se calculó antes de este dato: recalcula para traerlo">
        —
      </span>
    );
  }
  const temporal = String(tipo).toLowerCase() === 'temporal';
  return (
    <span
      className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium whitespace-nowrap ${
        temporal
          ? 'bg-amber-200 text-amber-900 dark:bg-amber-500/30 dark:text-amber-100'
          : 'bg-muted text-muted-foreground'
      }`}
    >
      {tipo}
    </span>
  );
};

const NominaDetallePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const usuario = { id: user?._id || user?.id, nombre: user?.nombre || user?.name || user?.email };

  const [nomina, setNomina] = useState(null);
  const [loading, setLoading] = useState(true);
  const [trabajando, setTrabajando] = useState('');
  const [texto, setTexto] = useState('');
  const [soloAvisos, setSoloAvisos] = useState(false);
  const [abierta, setAbierta] = useState(null);
  const [confirmar, setConfirmar] = useState(null); // 'anular' | 'eliminar'
  const [motivo, setMotivo] = useState('');

  const cargar = async () => {
    try {
      setNomina(await getNomina(id));
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, [id]);

  const accion = async (nombre, fn, exito) => {
    setTrabajando(nombre);
    try {
      const r = await fn();
      if (r && r._id) setNomina(r);
      if (exito) toast({ title: exito });
      return true;
    } catch (e) {
      toast({ title: 'No se pudo completar', description: e.message, variant: 'destructive' });
      return false;
    } finally {
      setTrabajando('');
    }
  };

  const lineas = useMemo(() => {
    const q = texto.trim().toLowerCase();
    return (nomina?.lineas || []).filter(
      (l) =>
        (!q || `${l.empleado?.nombre} ${l.empleado?.cedula || ''} ${(l.centros || []).map((c) => c.nombre).join(' ')}`.toLowerCase().includes(q)) &&
        (!soloAvisos || (l.advertencias || []).length > 0),
    );
  }, [nomina, texto, soloAvisos]);

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Cargando nómina…</div>;
  }
  if (!nomina) {
    return (
      <div className="p-8 text-center space-y-3">
        <p className="text-muted-foreground">No se encontró la nómina.</p>
        <Button variant="outline" onClick={() => navigate('/personal/nomina')}>Volver</Button>
      </div>
    );
  }

  const lineaAbierta = (nomina.lineas || []).find((l) => l._id === abierta) || null;
  const temporales = lineas.filter(esTemporal).length;
  const t = nomina.totales || {};
  const h = t.horas || {};
  const enBorrador = nomina.estado === 'borrador';
  const pendientes = nomina.pendientesAutorizar || 0;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <div className="space-y-4">
        {/* Encabezado */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <Button variant="ghost" size="icon" onClick={() => navigate('/personal/nomina')} title="Volver">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold">{nomina.nombre}</h2>
                <Badge variant={ESTADOS[nomina.estado]?.variant}>{ESTADOS[nomina.estado]?.label}</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {TIPOS_NOMINA[nomina.tipo]} · {fechaCorta(nomina.desde)} – {fechaCorta(nomina.hasta)} · {nomina.horasBase} h base ·{' '}
                {(nomina.centros || []).length ? nomina.centros.map((c) => c.nombre).join(', ') : 'Todas las sedes'}
              </p>
              {nomina.calculadaEn && (
                <p className="text-xs text-muted-foreground">
                  Calculada {new Date(nomina.calculadaEn).toLocaleString('es-CO')}
                  {nomina.aprobadoPor?.fecha && ` · Aprobada por ${nomina.aprobadoPor.nombre || '—'} el ${new Date(nomina.aprobadoPor.fecha).toLocaleDateString('es-CO')}`}
                  {nomina.pagadoPor?.fecha && ` · Pagada el ${new Date(nomina.pagadoPor.fecha).toLocaleDateString('es-CO')}`}
                  {nomina.anuladoPor?.fecha && ` · Anulada: ${nomina.anuladoPor.motivo || ''}`}
                </p>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => exportarNominaExcel(nomina)}>
              <FileSpreadsheet className="mr-2 h-4 w-4" /> Excel
            </Button>
            {enBorrador && (
              <>
                <Button
                  variant="outline"
                  disabled={!!trabajando}
                  onClick={() => accion('recalcular', () => recalcularNomina(id), 'Nómina recalculada')}
                >
                  {trabajando === 'recalcular' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
                  Recalcular
                </Button>
                <Button
                  disabled={!!trabajando}
                  title={pendientes ? 'Se recalcula y se valida que no queden registros pendientes' : ''}
                  onClick={() =>
                    accion('aprobar', () => cambiarEstadoNomina(id, 'aprobar', { usuario }), 'Nómina aprobada').then(
                      (ok) => !ok && cargar(),
                    )
                  }
                >
                  {trabajando === 'aprobar' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
                  Aprobar
                </Button>
              </>
            )}
            {nomina.estado === 'aprobada' && (
              <>
                <Button variant="outline" disabled={!!trabajando} onClick={() => accion('reabrir', () => cambiarEstadoNomina(id, 'reabrir'), 'Devuelta a borrador')}>
                  <Undo2 className="mr-2 h-4 w-4" /> A borrador
                </Button>
                <Button disabled={!!trabajando} onClick={() => accion('pagar', () => cambiarEstadoNomina(id, 'pagar', { usuario }), 'Marcada como pagada')}>
                  <Wallet className="mr-2 h-4 w-4" /> Marcar pagada
                </Button>
              </>
            )}
            {nomina.estado !== 'anulada' && (
              <Button variant="outline" className="text-destructive" disabled={!!trabajando} onClick={() => setConfirmar('anular')}>
                <Ban className="mr-2 h-4 w-4" /> Anular
              </Button>
            )}
            {enBorrador && (
              <Button variant="ghost" className="text-destructive" disabled={!!trabajando} onClick={() => setConfirmar('eliminar')} title="Eliminar borrador">
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {enBorrador && nomina.desactualizada && (
          <div className="flex items-start gap-2 rounded-md border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900 dark:border-rose-500/40 dark:bg-rose-500/10 dark:text-rose-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Hay fichas con un salario distinto al usado en el último cálculo ({nomina.desactualizada.personas}):{' '}
              {nomina.desactualizada.ejemplos.join(' · ')}
              {nomina.desactualizada.personas > nomina.desactualizada.ejemplos.length ? '…' : ''}. Pulsa <b>Recalcular</b> para
              actualizar la nómina.
            </span>
          </div>
        )}

        {enBorrador && pendientes > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Hay <b>{pendientes}</b> registro(s) sin autorizar o sin salida. Se liquidan con las horas marcadas, pero la
              nómina no se puede aprobar hasta autorizarlos en Horas autorizadas y recalcular.
            </span>
          </div>
        )}

        {/* Totales */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat titulo="Personas" valor={t.personas ?? 0} detalle={t.conAdvertencias ? `${t.conAdvertencias} con advertencias` : 'Sin advertencias'} />
          <Stat titulo="Horas trabajadas" valor={horas(h.trabajadas)} detalle={`${horas(h.ordinarias)} ordinarias`} />
          <Stat titulo="Básico" valor={pesos(t.valores?.basico ?? t.valores?.ordinarias)} detalle={`Salario ÷ 30 × días · aux. transporte ${pesos(t.valores?.auxilioTransporte)}`} />
          <Stat titulo="Recargos" valor={pesos(t.valores?.recargos)} detalle={`${horas(sumaGrupo(h, 'Recargos'))} h con recargo`} />
          <Stat titulo="Extras" valor={pesos(t.valores?.extras)} detalle={`${horas(sumaGrupo(h, 'Extras'))} h sobre la base`} />
          <Stat titulo="Devengado" valor={pesos(t.devengado)} detalle={`Deducciones ${pesos(t.deducciones)}`} />
          <Stat titulo="Neto a pagar" valor={pesos(t.neto)} destacado />
          <Stat
            titulo="Costo total empresa"
            valor={pesos(t.costoEmpresa?.total)}
            detalle={`Aportes ${pesos(t.costoEmpresa?.aportes?.total)} · prestaciones ${pesos(t.costoEmpresa?.prestaciones?.total)}`}
          />
        </div>

        {/* Horas y valores por tipo, agrupados */}
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-4">
          {GRUPOS_HORAS.map((g) => (
            <div key={g.grupo} className="rounded-lg border bg-card p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">{g.grupo}</div>
              <div className="space-y-1 text-sm">
                {g.tipos.map((tipo) => (
                  <div key={tipo.key} className="flex justify-between gap-2">
                    <span className={h[tipo.key] ? '' : 'text-muted-foreground'}>{tipo.largo}</span>
                    <span className="tabular-nums whitespace-nowrap">
                      {horas(h[tipo.key])} h · {pesos(t.valores?.[tipo.key])}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Filtros */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Buscar persona, cédula o sede" className="pl-8" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={soloAvisos} onCheckedChange={(v) => setSoloAvisos(!!v)} />
            Solo con advertencias
          </label>
          <span className="text-sm text-muted-foreground">
            {lineas.length} de {(nomina.lineas || []).length}
            {temporales > 0 && <> · <span className="text-amber-700 dark:text-amber-300">{temporales} temporal(es)</span></>}
          </span>
        </div>

        {/* Personas */}
        <div className="rounded-lg border bg-card overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead rowSpan={2} className="min-w-[240px] sticky left-0 z-10 bg-card align-bottom">Persona</TableHead>
                <TableHead rowSpan={2} className="align-bottom">Tipo nómina</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Días</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Base</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Trab.</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Ord.</TableHead>
                <TableHead colSpan={3} className="text-center border-l">Recargos (h)</TableHead>
                <TableHead colSpan={4} className="text-center border-l">Extras (h)</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom border-l">Ausen.</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom border-l">Básico</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Aux. transp.</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Recargos</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Extras</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Devengado</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom">Deduc.</TableHead>
                <TableHead rowSpan={2} className="text-right align-bottom sticky right-0 z-10 bg-card shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.4)]">Neto</TableHead>
              </TableRow>
              <TableRow className="hover:bg-transparent">
                <TableHead className="text-right border-l">Noct.</TableHead>
                <TableHead className="text-right" title="Dominical diurno / nocturno">Dom. D/N</TableHead>
                <TableHead className="text-right" title="Festivo diurno / nocturno">Fest. D/N</TableHead>
                <TableHead className="text-right border-l">Diurna</TableHead>
                <TableHead className="text-right">Noct.</TableHead>
                <TableHead className="text-right" title="Extra dominical diurna / nocturna">Dom. D/N</TableHead>
                <TableHead className="text-right" title="Extra festiva diurna / nocturna">Fest. D/N</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lineas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={21} className="text-center text-muted-foreground">Sin personas en este filtro.</TableCell>
                </TableRow>
              ) : (
                lineas.map((l) => {
                  const hh = l.horas || {};
                  const avisos = (l.advertencias || []).length;
                  const temporal = esTemporal(l);
                  // Las celdas fijas (izquierda y derecha) tapan el fondo de la fila, así que
                  // llevan el mismo tono; opaco, para que no se vea lo que pasa por debajo.
                  const fondoFijo = temporal ? 'bg-amber-50 dark:bg-amber-950' : 'bg-card';
                  return (
                    <Fragment key={l._id}>
                      <TableRow
                        className={`cursor-pointer ${
                          temporal ? 'bg-amber-50 hover:bg-amber-100 dark:bg-amber-950 dark:hover:bg-amber-900' : ''
                        }`}
                        onClick={() => setAbierta(l._id)}
                      >
                        <TableCell className={`sticky left-0 z-10 ${fondoFijo}`}>
                          <div className="font-medium flex items-center gap-1 whitespace-nowrap">
                            {l.empleado?.nombre}
                            {avisos > 0 && (
                              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title={(l.advertencias || []).join('\n')} />
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground whitespace-nowrap">
                            {l.empleado?.cedula || 'Sin cédula'} · {(l.centros || []).map((c) => c.nombre).join(', ') || 'Sin marcaciones'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <TipoNomina tipo={l.empleado?.tipoNomina} />
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums ${l.diasPersonalizados ? 'text-primary font-medium' : ''}`}
                          title={l.diasPersonalizados ? 'Días fijados a mano' : undefined}
                        >
                          {horas(l.diasLaborados)}
                        </TableCell>
                        <Num v={l.horasBase} marcado={l.horasBasePersonalizada} />
                        <Num v={hh.trabajadas} />
                        <Num v={hh.ordinarias} />
                        <Num v={hh.recargoNocturno} borde />
                        <Par a={hh.recargoDominicalDiurno} b={hh.recargoDominicalNocturno} />
                        <Par a={hh.recargoFestivoDiurno} b={hh.recargoFestivoNocturno} />
                        <Num v={hh.extraDiurna} borde />
                        <Num v={hh.extraNocturna} />
                        <Par a={hh.extraDominicalDiurna} b={hh.extraDominicalNocturna} />
                        <Par a={hh.extraFestivaDiurna} b={hh.extraFestivaNocturna} />
                        <Num v={sumaGrupo(hh, 'Ausencias')} borde />
                        <Money v={l.valores?.ordinarias} borde />
                        <Money v={l.valores?.auxilioTransporte} />
                        <Money v={l.valores?.recargos} />
                        <Money v={l.valores?.extras} />
                        <Money v={l.totalDevengado} />
                        <Money v={l.totalDeducciones} />
                        <TableCell
                          className={`text-right tabular-nums whitespace-nowrap font-medium sticky right-0 z-10 ${fondoFijo} shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.4)]`}
                        >
                          {pesos(l.neto)}
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={!!lineaAbierta} onOpenChange={(v) => !v && setAbierta(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          {lineaAbierta && (
            <>
              <DialogHeader>
                <DialogTitle>{lineaAbierta.empleado?.nombre}</DialogTitle>
                <DialogDescription>
                  {lineaAbierta.empleado?.cedula || 'Sin cédula'} · {lineaAbierta.empleado?.rol || 'Sin cargo'} ·{' '}
                  {lineaAbierta.empleado?.tipoNomina || 'Tipo de nómina sin definir'} ·{' '}
                  {(lineaAbierta.centros || []).map((c) => c.nombre).join(', ') || 'Sin marcaciones'} ·{' '}
                  {horas(lineaAbierta.horas?.trabajadas)} h trabajadas
                </DialogDescription>
              </DialogHeader>
              <LineaDetalle
                linea={lineaAbierta}
                editable={enBorrador}
                horasBaseNomina={nomina.horasBase}
                onGuardar={(payload) =>
                  accion('linea', () => actualizarLineaNomina(id, lineaAbierta._id, payload), 'Persona recalculada')
                }
                guardando={trabajando === 'linea'}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmar} onOpenChange={(v) => !v && setConfirmar(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{confirmar === 'anular' ? 'Anular nómina' : 'Eliminar borrador'}</DialogTitle>
            <DialogDescription>
              {confirmar === 'anular'
                ? 'La nómina queda en el historial como anulada y su periodo se libera para generar otra.'
                : 'Se borra la nómina y todas sus líneas. Esta acción no se puede deshacer.'}
            </DialogDescription>
          </DialogHeader>
          {confirmar === 'anular' && (
            <div className="space-y-2">
              <Label htmlFor="nom-motivo">Motivo</Label>
              <Input id="nom-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. periodo mal definido" />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmar(null)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={confirmar === 'anular' && !motivo.trim()}
              onClick={async () => {
                if (confirmar === 'anular') {
                  const ok = await accion('anular', () => cambiarEstadoNomina(id, 'anular', { usuario, motivo }), 'Nómina anulada');
                  if (ok) setConfirmar(null);
                } else {
                  const ok = await accion('eliminar', () => eliminarNomina(id), 'Borrador eliminado');
                  if (ok) navigate('/personal/nomina');
                }
              }}
            >
              {confirmar === 'anular' ? 'Anular' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

const Num = ({ v, marcado, borde }) => (
  <TableCell
    className={`text-right tabular-nums ${marcado ? 'text-primary font-medium' : ''} ${borde ? 'border-l' : ''}`}
    title={marcado ? 'Horas base personalizadas' : undefined}
  >
    {horas(v)}
  </TableCell>
);

const Par = ({ a, b }) => (
  <TableCell className="text-right tabular-nums whitespace-nowrap">
    {horas(a)} / {horas(b)}
  </TableCell>
);

const Money = ({ v, borde }) => (
  <TableCell className={`text-right tabular-nums whitespace-nowrap ${borde ? 'border-l' : ''}`}>{pesos(v)}</TableCell>
);

const Stat = ({ titulo, valor, detalle, destacado }) => (
  <div className={`rounded-lg border p-3 ${destacado ? 'bg-primary/10 border-primary/30' : 'bg-card'}`}>
    <div className="text-xs text-muted-foreground">{titulo}</div>
    <div className="text-lg font-bold tabular-nums">{valor}</div>
    {detalle && <div className="text-xs text-muted-foreground">{detalle}</div>}
  </div>
);

/** Detalle de una persona: días, valores, conceptos y (en borrador) horas base y ajustes. */
const LineaDetalle = ({ linea, editable, horasBaseNomina, onGuardar, guardando }) => {
  const [horasBase, setHorasBase] = useState(linea.horasBasePersonalizada ? String(linea.horasBase) : '');
  const [diasLab, setDiasLab] = useState(linea.diasPersonalizados ? String(linea.diasLaborados) : '');
  const [ajustes, setAjustes] = useState(linea.ajustes || []);

  useEffect(() => {
    setHorasBase(linea.horasBasePersonalizada ? String(linea.horasBase) : '');
    setDiasLab(linea.diasPersonalizados ? String(linea.diasLaborados) : '');
    setAjustes(linea.ajustes || []);
  }, [linea]);

  const valores = linea.valores || {};
  const conceptos = linea.conceptos || [];

  return (
    <div className="space-y-4">
      {(linea.advertencias || []).length > 0 && (
        <ul className="rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900 list-disc pl-6 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          {linea.advertencias.map((a, i) => (
            <li key={i}>{a}</li>
          ))}
        </ul>
      )}

      <div className="grid gap-4 lg:grid-cols-2 items-start">
          <div className="rounded-md border bg-card p-3 text-sm space-y-1">
            <div className="flex justify-between text-muted-foreground">
              <span>Salario · valor hora</span>
              <span className="tabular-nums">{pesos(linea.salario)} · {pesos(linea.valorHora)}</span>
            </div>
            <div className="flex justify-between">
              <span>
                Básico ({horas(linea.diasLaborados)} días · {horas(linea.horas?.ordinarias)} h ordinarias)
              </span>
              <span className="tabular-nums">{pesos(valores.basico ?? valores.ordinarias)}</span>
            </div>
            {valores.auxilioTransporte > 0 && (
              <div className="flex justify-between">
                <span>Auxilio de transporte</span>
                <span className="tabular-nums">{pesos(valores.auxilioTransporte)}</span>
              </div>
            )}
            {GRUPOS_HORAS.filter((g) => g.grupo !== 'Ordinarias').map((g) => {
              const tipos = g.tipos.filter((t) => linea.horas?.[t.key]);
              if (!tipos.length) return null;
              return (
                <div key={g.grupo} className="pt-1">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{g.grupo}</div>
                  {tipos.map((t) => (
                    <div key={t.key} className="flex justify-between">
                      <span>{t.largo} ({horas(linea.horas?.[t.key])} h)</span>
                      <span className="tabular-nums">{pesos(valores[t.key])}</span>
                    </div>
                  ))}
                </div>
              );
            })}
            {conceptos.length > 0 && (
              <div className="pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Conceptos</div>
            )}
            {conceptos.map((c, i) => (
              <div key={i} className="flex justify-between">
                <span>{c.naturaleza === 'deduccion' ? '(−) ' : '(+) '}{c.nombre}{c.tipoCalculo === 'porcentaje' ? ` ${c.valor} %` : ''}</span>
                <span className="tabular-nums">{pesos(c.montoCalculado)}</span>
              </div>
            ))}
            {(linea.ajustes || []).map((a, i) => (
              <div key={`a${i}`} className="flex justify-between text-primary">
                <span>{a.naturaleza === 'deduccion' ? '(−) ' : '(+) '}{a.nombre}</span>
                <span className="tabular-nums">{pesos(a.valor)}</span>
              </div>
            ))}
            <div className="flex justify-between border-t pt-1 font-semibold">
              <span>Neto</span>
              <span className="tabular-nums">{pesos(linea.neto)}</span>
            </div>
            {linea.costoEmpresa && (
              <div className="border-t pt-1 mt-1 space-y-0.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Costo empresa (no se descuenta a la persona)
                </div>
                {[
                  ['Pensión', linea.costoEmpresa.aportes?.pension],
                  ['ARL', linea.costoEmpresa.aportes?.arl],
                  ['Caja de compensación', linea.costoEmpresa.aportes?.caja],
                  ['Salud empleador', linea.costoEmpresa.aportes?.salud],
                  ['ICBF', linea.costoEmpresa.aportes?.icbf],
                  ['SENA', linea.costoEmpresa.aportes?.sena],
                  ['Prima (provisión)', linea.costoEmpresa.prestaciones?.prima],
                  ['Cesantías (provisión)', linea.costoEmpresa.prestaciones?.cesantias],
                  ['Intereses de cesantías', linea.costoEmpresa.prestaciones?.interesesCesantias],
                  ['Vacaciones (provisión)', linea.costoEmpresa.prestaciones?.vacaciones],
                ]
                  .filter(([, v]) => v)
                  .map(([nombre, v]) => (
                    <div key={nombre} className="flex justify-between text-muted-foreground">
                      <span>{nombre}</span>
                      <span className="tabular-nums">{pesos(v)}</span>
                    </div>
                  ))}
                <div className="flex justify-between font-semibold">
                  <span>Costo total empresa</span>
                  <span className="tabular-nums">{pesos(linea.costoEmpresa.total)}</span>
                </div>
              </div>
            )}
          </div>

          {editable && (
            <div className="rounded-md border bg-card p-3 space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Días a pagar en el básico</Label>
                <Input
                  type="number"
                  min="0"
                  max="31"
                  step="0.5"
                  value={diasLab}
                  onChange={(e) => setDiasLab(e.target.value)}
                  placeholder={`${horas(linea.diasLaborados)} (calculado)`}
                />
                <p className="text-[11px] text-muted-foreground">
                  Vacío = días del periodo menos los anteriores al ingreso. Incapacidades y permisos no remunerados se
                  descuentan aparte.
                </p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Horas base de esta persona</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={horasBase}
                  onChange={(e) => setHorasBase(e.target.value)}
                  placeholder={`${horasBaseNomina} (de la nómina)`}
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Ajustes manuales</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setAjustes([...ajustes, { nombre: '', naturaleza: 'devengado', valor: 0 }])}
                  >
                    <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
                  </Button>
                </div>
                {ajustes.map((a, i) => (
                  <div key={i} className="flex gap-1">
                    <Input
                      value={a.nombre}
                      placeholder="Bonificación, préstamo…"
                      onChange={(e) => setAjustes(ajustes.map((x, j) => (j === i ? { ...x, nombre: e.target.value } : x)))}
                    />
                    <Select
                      value={a.naturaleza}
                      onValueChange={(v) => setAjustes(ajustes.map((x, j) => (j === i ? { ...x, naturaleza: v } : x)))}
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="devengado">+</SelectItem>
                        <SelectItem value="deduccion">−</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      className="w-28"
                      value={a.valor}
                      onChange={(e) => setAjustes(ajustes.map((x, j) => (j === i ? { ...x, valor: e.target.value } : x)))}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => setAjustes(ajustes.filter((_, j) => j !== i))}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                size="sm"
                disabled={guardando}
                onClick={() =>
                  onGuardar({
                    horasBase: horasBase === '' ? null : Number(horasBase),
                    diasLaborados: diasLab === '' ? null : Number(diasLab),
                    ajustes: ajustes.map((a) => ({ ...a, valor: Number(a.valor) || 0 })),
                  })
                }
              >
                {guardando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Guardar y recalcular
              </Button>
            </div>
          )}
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Sede</TableHead>
              <TableHead>Entrada</TableHead>
              <TableHead>Salida</TableHead>
              <TableHead className="text-right">Horas</TableHead>
              <TableHead className="text-right">Ord.</TableHead>
              <TableHead className="text-right">Extras</TableHead>
              <TableHead>Recargos y extras</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(linea.dias || []).map((d, i) => (
              <TableRow key={i}>
                <TableCell className="whitespace-nowrap">
                  <span className="capitalize text-muted-foreground mr-1">{diaSemana(d.fecha)}</span>
                  {fechaCorta(d.fecha)}
                  {TIPO_DIA[d.tipoDia] && (
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${TIPO_DIA[d.tipoDia].className}`}>
                      {TIPO_DIA[d.tipoDia].label}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">{d.centro || '—'}</TableCell>
                <TableCell>{d.entrada || '—'}</TableCell>
                <TableCell>{d.salida || '—'}</TableCell>
                <TableCell className="text-right tabular-nums">{horas(d.horas)}</TableCell>
                <TableCell className="text-right tabular-nums">{horas(d.ordinarias)}</TableCell>
                <TableCell className="text-right tabular-nums">{horas(d.extras)}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(d.detalle || {}).map(([k, v]) => (
                      <span
                        key={k}
                        className={`rounded px-1.5 py-0.5 text-[11px] whitespace-nowrap ${
                          k.startsWith('extra')
                            ? 'bg-rose-100 text-rose-800 dark:bg-rose-500/20 dark:text-rose-200'
                            : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-500/20 dark:text-indigo-200'
                        }`}
                      >
                        {ETIQUETA_TIPO[k] || k} {horas(v)} h
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell>
                  {d.ausencia ? (
                    <Badge variant="outline">
                      {d.ausencia.tipo === 'incapacidad' ? 'Incapacidad' : d.ausencia.remunerado ? 'Permiso' : 'Permiso no rem.'}
                    </Badge>
                  ) : d.repetida ? (
                    <Badge variant="destructive" title="Se cruza con otra marcación del mismo día">Repetida</Badge>
                  ) : d.sinSalida ? (
                    <Badge variant="destructive">Sin salida</Badge>
                  ) : d.pendiente ? (
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200">Pendiente</Badge>
                  ) : (
                    <Badge variant="outline" className="text-emerald-700">Autorizado</Badge>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default NominaDetallePage;
